import React, { useCallback, useEffect, useState } from 'react';
import { Navigate, Link, useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, FileText, Loader2, Undo2, RotateCcw,
  User, Building2, Copy, ExternalLink, Eye, Send,
} from 'lucide-react';
import { toast } from 'sonner';
import { useAdminAuth } from './AdminAuthContext';
import { adminApi } from './adminApi';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../components/ui/dialog';

const BACKEND = process.env.REACT_APP_BACKEND_URL;

function money(n) {
  const v = Number(n);
  if (Number.isNaN(v)) return '—';
  return v.toLocaleString('en-IN', { style: 'currency', currency: 'INR' });
}

export function AdminInvoiceDetailPage() {
  const { admin, loading } = useAdminAuth();
  const { id } = useParams();
  const navigate = useNavigate();
  const [invoice, setInvoice] = useState(null);
  const [fetching, setFetching] = useState(true);
  const [cnOpen, setCnOpen] = useState(false);
  const [correctOpen, setCorrectOpen] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(async () => {
    setFetching(true);
    try {
      const r = await adminApi.get(`/admin/invoicing/invoices/${id}`);
      setInvoice(r.data);
    } catch { toast.error('Invoice not found'); }
    finally { setFetching(false); }
  }, [id]);

  useEffect(() => { if (admin && id) refresh(); }, [admin, id, refresh]);

  if (loading) return null;
  if (!admin) return <Navigate to="/admin/login" replace />;

  const doResend = async () => {
    setBusy(true);
    try {
      await adminApi.post(`/admin/invoicing/invoices/${id}/resend-email`);
      toast.success('Delivery email queued');
      setPreviewOpen(false);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Resend failed'); }
    finally { setBusy(false); }
  };

  const openPreview = async () => {
    setPreviewOpen(true);
    setPreview(null);
    setPreviewLoading(true);
    try {
      const r = await adminApi.get(`/admin/invoicing/invoices/${id}/email-preview`);
      setPreview(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Preview failed');
      setPreviewOpen(false);
    } finally { setPreviewLoading(false); }
  };

  /**
   * Open an authenticated admin PDF endpoint in a new tab.
   *
   * The browser's default behaviour on `<a target="_blank">` is a
   * fresh top-level GET with NO Authorization header — which hits
   * our admin-JWT guard and renders `{"detail":"Admin authentication required"}`
   * in a blank new tab. So instead we fetch the bytes through adminApi
   * (which attaches the bearer), wrap them in a Blob, and open the
   * blob: URL. If the browser blocks the popup we fall back to a
   * download so the admin never sees a dead-end.
   */
  const openAdminPdf = async (url, fallbackName) => {
    try {
      const r = await adminApi.get(url, { responseType: 'blob' });
      const blob = new Blob([r.data], { type: 'application/pdf' });
      const blobUrl = URL.createObjectURL(blob);
      const w = window.open(blobUrl, '_blank', 'noopener,noreferrer');
      if (!w) {
        const a = document.createElement('a');
        a.href = blobUrl;
        a.download = fallbackName || 'invoice.pdf';
        document.body.appendChild(a); a.click(); a.remove();
      }
      setTimeout(() => URL.revokeObjectURL(blobUrl), 60_000);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Could not open PDF');
    }
  };

  const openInvoicePdf = () => openAdminPdf(
    `/admin/invoicing/invoices/${id}/pdf`,
    `invoice-${invoice?.invoice_number || id}.pdf`,
  );

  const doCreditNote = async (reason) => {
    setBusy(true);
    try {
      const r = await adminApi.post(`/admin/invoicing/invoices/${id}/credit-note`, { reason });
      toast.success(`Credit note ${r.data.credit_note_number} issued`);
      setCnOpen(false);
      await refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Credit note failed'); }
    finally { setBusy(false); }
  };

  const doCorrectAndReissue = async (body) => {
    setBusy(true);
    try {
      const r = await adminApi.post(`/admin/invoicing/invoices/${id}/correct-and-reissue`, body);
      toast.success(`Re-issued as ${r.data.reissued_invoice_number}`);
      setCorrectOpen(false);
      navigate(`/admin/invoicing/invoices/${r.data.reissued_invoice_id}`);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Re-issue failed'); }
    finally { setBusy(false); }
  };

  const copy = async (t, l) => {
    try { await navigator.clipboard.writeText(t); toast.success(`${l} copied`); }
    catch { toast.error('Copy failed'); }
  };

  return (
    <div
      data-testid="admin-invoice-detail-page"
      className="min-h-screen"
      style={{ background: 'radial-gradient(circle at 0% 0%, #1e293b 0%, #0f172a 50%, #020617 100%)' }}
    >
      <header className="border-b border-slate-800 bg-slate-950/60 backdrop-blur-xl">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center gap-3">
          <Link to="/admin/invoicing" className="w-9 h-9 rounded-lg bg-slate-900 border border-slate-800 flex items-center justify-center hover:bg-slate-800" data-testid="invoice-detail-back">
            <ArrowLeft className="w-4 h-4 text-slate-300" />
          </Link>
          <div className="flex-1">
            <div className="text-white font-semibold flex items-center gap-2">
              {fetching ? 'Loading…' : (invoice?.invoice_number || 'Draft invoice')}
              {invoice?.status && (
                <span className="text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded border border-slate-600 text-slate-300">
                  {invoice.status}
                </span>
              )}
            </div>
            <div className="text-xs text-slate-400">
              {invoice?.invoice_date} · {invoice?.supply_type} · {invoice?.place_of_supply}
            </div>
          </div>
          {invoice && (
            <div className="flex gap-2">
              <a
                href="#"
                onClick={(e) => { e.preventDefault(); openInvoicePdf(); }}
                data-testid="invoice-pdf-link"
                className="text-xs text-amber-300 hover:text-amber-200 border border-slate-700 rounded-md px-3 py-1.5 hover:border-amber-500/40 inline-flex items-center cursor-pointer"
              >
                <FileText className="w-3.5 h-3.5 mr-1.5" /> Open PDF <ExternalLink className="w-3 h-3 ml-1" />
              </a>
              <Button onClick={openPreview} disabled={busy} variant="outline" size="sm" data-testid="invoice-preview-email" className="border-slate-700 bg-slate-900 text-slate-200 hover:bg-slate-800">
                <Eye className="w-3.5 h-3.5 mr-1.5" /> Preview & send
              </Button>
              {invoice.status === 'issued' && (
                <>
                  <Button onClick={() => setCorrectOpen(true)} disabled={busy} size="sm" data-testid="invoice-correct-reissue-btn" className="bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold">
                    <RotateCcw className="w-3.5 h-3.5 mr-1.5" /> Correct & re-issue
                  </Button>
                  <Button onClick={() => setCnOpen(true)} disabled={busy} variant="outline" size="sm" data-testid="invoice-cn-btn" className="border-rose-500/40 bg-slate-900 text-rose-300 hover:bg-rose-500/10">
                    <Undo2 className="w-3.5 h-3.5 mr-1.5" /> Credit note
                  </Button>
                </>
              )}
            </div>
          )}
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-6 space-y-4">
        {fetching ? (
          <div className="p-10 flex items-center justify-center text-slate-400 gap-2">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading…
          </div>
        ) : invoice && (
          <>
            <section className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="invoice-parties">
              <div className="bg-slate-900/70 border border-slate-800 rounded-xl p-5">
                <div className="flex items-center gap-2 text-slate-400 text-xs uppercase tracking-wider mb-3">
                  <Building2 className="w-3.5 h-3.5" /> Seller
                </div>
                <div className="text-white font-medium">{invoice.seller_snapshot?.legal_name}</div>
                {invoice.seller_snapshot?.trade_name && invoice.seller_snapshot.trade_name !== invoice.seller_snapshot.legal_name && (
                  <div className="text-xs text-slate-400 italic">{invoice.seller_snapshot.trade_name}</div>
                )}
                <div className="text-xs text-slate-400 mt-2 whitespace-pre-line">
                  {invoice.seller_snapshot?.address}
                  {invoice.seller_snapshot?.city && `, ${invoice.seller_snapshot.city}`}
                  {invoice.seller_snapshot?.state && `, ${invoice.seller_snapshot.state}`}
                  {invoice.seller_snapshot?.pincode && ` — ${invoice.seller_snapshot.pincode}`}
                </div>
                <div className="mt-3 space-y-1 text-xs">
                  <Row label="GSTIN" value={invoice.seller_snapshot?.gstin} mono onCopy={copy} />
                  {invoice.seller_snapshot?.pan && <Row label="PAN" value={invoice.seller_snapshot.pan} mono />}
                  {invoice.seller_snapshot?.contact_phone && <Row label="Phone" value={invoice.seller_snapshot.contact_phone} />}
                  {invoice.seller_snapshot?.contact_email && <Row label="Email" value={invoice.seller_snapshot.contact_email} />}
                </div>
              </div>

              <div className="bg-slate-900/70 border border-slate-800 rounded-xl p-5">
                <div className="flex items-center gap-2 text-slate-400 text-xs uppercase tracking-wider mb-3">
                  <User className="w-3.5 h-3.5" /> Buyer
                </div>
                <div className="text-white font-medium">{invoice.buyer?.name}</div>
                <div className="text-xs text-slate-400 mt-2 whitespace-pre-line">
                  {invoice.buyer?.address}
                  {(invoice.buyer?.city || invoice.buyer?.state) && `\n${[invoice.buyer.city, invoice.buyer.state].filter(Boolean).join(', ')}`}
                  {invoice.buyer?.pincode && ` — ${invoice.buyer.pincode}`}
                </div>
                <div className="mt-3 space-y-1 text-xs">
                  {invoice.buyer?.gstin && <Row label="GSTIN" value={invoice.buyer.gstin} mono onCopy={copy} />}
                  {invoice.buyer?.email && <Row label="Email" value={invoice.buyer.email} />}
                  {invoice.buyer?.phone && <Row label="Phone" value={invoice.buyer.phone} />}
                </div>
              </div>
            </section>

            <section className="bg-slate-900/70 border border-slate-800 rounded-xl overflow-hidden" data-testid="invoice-lines">
              <table className="w-full text-sm">
                <thead className="bg-slate-950/60">
                  <tr className="text-[10px] uppercase tracking-wider text-slate-400">
                    <th className="px-4 py-2 text-left">HSN/SAC</th>
                    <th className="px-4 py-2 text-left">Description</th>
                    <th className="px-4 py-2 text-right">Qty</th>
                    <th className="px-4 py-2 text-right">Taxable</th>
                    {invoice.is_intra_state ? (
                      <>
                        <th className="px-4 py-2 text-right">CGST</th>
                        <th className="px-4 py-2 text-right">SGST</th>
                      </>
                    ) : (
                      <th className="px-4 py-2 text-right">IGST</th>
                    )}
                    <th className="px-4 py-2 text-right">Line total</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-800/70">
                  {invoice.lines?.map((line, i) => (
                    <tr key={i} className="text-slate-200">
                      <td className="px-4 py-2.5 font-mono text-xs text-slate-400">{line.hsn_sac}</td>
                      <td className="px-4 py-2.5">{line.description}</td>
                      <td className="px-4 py-2.5 text-right font-mono tabular-nums">{Number(line.quantity)}</td>
                      <td className="px-4 py-2.5 text-right font-mono tabular-nums">{money(line.taxable_value)}</td>
                      {invoice.is_intra_state ? (
                        <>
                          <td className="px-4 py-2.5 text-right font-mono tabular-nums">{money(line.cgst_amount)}</td>
                          <td className="px-4 py-2.5 text-right font-mono tabular-nums">{money(line.sgst_amount)}</td>
                        </>
                      ) : (
                        <td className="px-4 py-2.5 text-right font-mono tabular-nums">{money(line.igst_amount)}</td>
                      )}
                      <td className="px-4 py-2.5 text-right font-mono tabular-nums text-white">{money(line.line_total)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="flex justify-end p-4 bg-slate-950/40 border-t border-slate-800">
                <table className="text-sm text-slate-300 w-full max-w-xs">
                  <tbody>
                    <Total label="Subtotal" value={invoice.subtotal} />
                    {invoice.is_intra_state ? (
                      <>
                        <Total label="CGST" value={invoice.total_cgst} />
                        <Total label="SGST" value={invoice.total_sgst} />
                      </>
                    ) : (
                      <Total label="IGST" value={invoice.total_igst} />
                    )}
                    {Number(invoice.round_off) !== 0 && <Total label="Round off" value={invoice.round_off} />}
                    <Total label="Grand total" value={invoice.grand_total} strong />
                  </tbody>
                </table>
              </div>
            </section>

            {invoice.credit_note_ids?.length > 0 && (
              <section className="bg-rose-500/5 border border-rose-500/30 rounded-xl p-4" data-testid="invoice-cn-refs">
                <div className="text-rose-300 text-xs uppercase tracking-wider font-bold mb-1">Credit notes issued</div>
                <div className="text-sm text-slate-300">
                  This invoice has {invoice.credit_note_ids.length} credit note{invoice.credit_note_ids.length === 1 ? '' : 's'} against it.
                  See <Link to="/admin/invoicing?subtab=credit_notes" className="text-rose-300 hover:text-rose-200 underline">Credit Notes tab</Link>.
                </div>
              </section>
            )}
          </>
        )}
      </main>

      <CreditNoteDialog open={cnOpen} onClose={() => setCnOpen(false)} onConfirm={doCreditNote} busy={busy} />
      <CorrectReissueDialog
        open={correctOpen}
        invoice={invoice}
        onClose={() => setCorrectOpen(false)}
        onConfirm={doCorrectAndReissue}
        busy={busy}
      />
      <EmailPreviewDialog
        open={previewOpen}
        preview={preview}
        loading={previewLoading}
        busy={busy}
        onClose={() => setPreviewOpen(false)}
        onSend={doResend}
      />
    </div>
  );
}


function EmailPreviewDialog({ open, preview, loading, busy, onClose, onSend }) {
  const [mode, setMode] = useState('html');
  useEffect(() => { if (open) setMode('html'); }, [open]);

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        className="bg-slate-900 border-slate-700 text-slate-100 max-w-3xl max-h-[90vh] flex flex-col"
        data-testid="email-preview-dialog"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Eye className="w-4 h-4 text-amber-400" /> What the buyer will see
          </DialogTitle>
          <DialogDescription className="text-slate-400">
            Rendered from the live license-delivery template. Attachments are the exact PDFs the resend will fire.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center p-10 gap-2 text-slate-400">
            <Loader2 className="w-4 h-4 animate-spin" /> Rendering preview…
          </div>
        ) : preview ? (
          <div className="space-y-3 overflow-y-auto flex-1 pr-1">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-xs" data-testid="preview-meta">
              <MetaRow label="To" value={preview.recipient} />
              <MetaRow label="Subject" value={preview.subject} />
              <MetaRow label="Attachments" value={`${preview.attachments?.length || 0} file(s)`} />
            </div>

            <div className="flex items-center gap-1 bg-slate-800/50 border border-slate-700 rounded-lg p-1 w-fit" data-testid="preview-mode-tabs">
              {[
                { k: 'html', label: 'HTML' },
                { k: 'plain', label: 'Plain text' },
              ].map((t) => (
                <button
                  key={t.k}
                  type="button"
                  data-testid={`preview-mode-${t.k}`}
                  onClick={() => setMode(t.k)}
                  className={`text-xs px-3 py-1 rounded-md transition-colors ${
                    mode === t.k ? 'bg-amber-500/15 text-amber-300 border border-amber-500/30' : 'text-slate-300 hover:bg-slate-800'
                  }`}
                >
                  {t.label}
                </button>
              ))}
            </div>

            {mode === 'html' ? (
              <iframe
                srcDoc={preview.html_body}
                title="Email preview"
                data-testid="preview-html-iframe"
                sandbox=""
                className="w-full h-[50vh] rounded-md bg-white border border-slate-700"
              />
            ) : (
              <pre className="w-full h-[50vh] overflow-auto rounded-md bg-slate-950 border border-slate-800 p-4 text-[12px] text-slate-200 whitespace-pre-wrap" data-testid="preview-plain-body">
                {preview.plain_body}
              </pre>
            )}

            {preview.attachments?.length > 0 && (
              <div className="rounded-md border border-slate-700 divide-y divide-slate-800" data-testid="preview-attachments">
                {preview.attachments.map((a) => (
                  <div key={a.filename} className="flex items-center gap-3 px-3 py-2 text-sm">
                    <FileText className="w-4 h-4 text-amber-400" />
                    <div className="flex-1 min-w-0">
                      <div className="text-slate-200 font-mono text-xs truncate">{a.filename}</div>
                      <div className="text-[10px] text-slate-500 uppercase tracking-wider">{a.kind}</div>
                    </div>
                    <div className="text-xs text-slate-400 font-mono tabular-nums">
                      {a.size_bytes >= 1024 ? `${(a.size_bytes / 1024).toFixed(1)} KB` : `${a.size_bytes} B`}
                    </div>
                    {a.download_url && (
                      <a
                        href="#"
                        onClick={(e) => {
                          e.preventDefault();
                          // `download_url` already includes the /api prefix; strip it
                          // because adminApi has baseURL ending in /api.
                          const relative = a.download_url.replace(/^\/api/, '');
                          openAdminPdf(relative, a.filename);
                        }}
                        className="text-xs text-amber-300 hover:text-amber-200 cursor-pointer"
                        data-testid={`preview-attachment-open-${a.kind}`}
                      >
                        Open ↗
                      </a>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        ) : (
          <div className="p-8 text-center text-slate-500 text-sm">Preview unavailable.</div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy} className="border-slate-700 bg-slate-900 text-slate-200 hover:bg-slate-800">
            Cancel
          </Button>
          <Button
            onClick={onSend}
            disabled={busy || loading || !preview}
            data-testid="preview-send-btn"
            className="bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold"
          >
            {busy ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Send className="w-4 h-4 mr-1.5" />}
            Send to {preview?.recipient || 'buyer'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function MetaRow({ label, value }) {
  return (
    <div className="bg-slate-800/40 border border-slate-700 rounded-md px-3 py-2">
      <div className="text-[10px] uppercase tracking-wider text-slate-500">{label}</div>
      <div className="text-slate-200 truncate text-xs">{value || '—'}</div>
    </div>
  );
}


function Row({ label, value, mono, onCopy }) {
  return (
    <div className="flex items-center gap-2">
      <span className="text-slate-500 w-14 shrink-0">{label}</span>
      <span className={`text-slate-300 truncate ${mono ? 'font-mono' : ''}`}>{value}</span>
      {onCopy && (
        <button onClick={() => onCopy(value, label)} type="button" aria-label={`Copy ${label}`}>
          <Copy className="w-3 h-3 text-slate-500 hover:text-amber-300" />
        </button>
      )}
    </div>
  );
}


function Total({ label, value, strong }) {
  return (
    <tr>
      <td className={`py-1 text-right ${strong ? 'font-bold text-white' : 'text-slate-400'}`}>{label}</td>
      <td className={`py-1 pl-4 text-right font-mono tabular-nums ${strong ? 'text-white font-bold border-t border-slate-700 pt-2' : 'text-slate-200'}`}>
        {value !== undefined ? Number(value).toLocaleString('en-IN', { style: 'currency', currency: 'INR' }) : '—'}
      </td>
    </tr>
  );
}


function CreditNoteDialog({ open, onClose, onConfirm, busy }) {
  const [reason, setReason] = useState('');
  useEffect(() => { if (open) setReason(''); }, [open]);
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-slate-900 border-slate-700 text-slate-100" data-testid="cn-dialog">
        <DialogHeader>
          <DialogTitle>Issue credit note</DialogTitle>
          <DialogDescription className="text-slate-400">
            Full reversal of the invoice. The invoice number stays reserved for GSTR-1 docs-issued reporting.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          <Label className="text-slate-300 text-xs uppercase tracking-wider">Reason (required)</Label>
          <Textarea
            data-testid="cn-reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            className="bg-slate-800/50 border-slate-700 text-white"
            rows={3}
            placeholder="e.g. Payment refunded due to duplicate purchase"
          />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy} className="border-slate-700 bg-slate-900 text-slate-200 hover:bg-slate-800">Cancel</Button>
          <Button
            onClick={() => onConfirm(reason.trim())}
            disabled={busy || reason.trim().length < 3}
            data-testid="cn-confirm"
            className="bg-rose-500 hover:bg-rose-400 text-white font-semibold"
          >
            {busy ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Undo2 className="w-4 h-4 mr-1.5" />}
            Issue credit note
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function CorrectReissueDialog({ open, invoice, onClose, onConfirm, busy }) {
  const [form, setForm] = useState({});
  useEffect(() => {
    if (open && invoice) {
      setForm({
        reason: 'Buyer submitted corrected billing details after payment',
        buyer_name: invoice.buyer?.name || '',
        buyer_gstin: invoice.buyer?.gstin || '',
        buyer_email: invoice.buyer?.email || '',
        billing_address: invoice.buyer?.address || '',
        billing_state: invoice.buyer?.state || '',
        billing_pincode: invoice.buyer?.pincode || '',
        billing_city: invoice.buyer?.city || '',
        buyer_phone: invoice.buyer?.phone || '',
        resend_email: true,
      });
    }
  }, [open, invoice]);
  const set = (k) => (e) => setForm((p) => ({ ...p, [k]: e?.target?.value ?? e }));

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-slate-900 border-slate-700 text-slate-100 max-w-2xl" data-testid="reissue-dialog">
        <DialogHeader>
          <DialogTitle>Correct & re-issue invoice</DialogTitle>
          <DialogDescription className="text-slate-400">
            Issues a full-reversal credit note, then generates a fresh invoice with the corrected buyer details.
            Both PDFs are emailed to the buyer if the toggle below is on.
          </DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 max-h-[60vh] overflow-y-auto pr-1">
          <FieldRow label="Reason" value={form.reason} onChange={set('reason')} testId="reissue-reason" full />
          <FieldRow label="Buyer name" value={form.buyer_name} onChange={set('buyer_name')} testId="reissue-name" />
          <FieldRow label="Buyer GSTIN" value={form.buyer_gstin} onChange={set('buyer_gstin')} testId="reissue-gstin" mono />
          <FieldRow label="Buyer email" value={form.buyer_email} onChange={set('buyer_email')} testId="reissue-email" />
          <FieldRow label="Buyer phone" value={form.buyer_phone} onChange={set('buyer_phone')} testId="reissue-phone" />
          <FieldRow label="Billing address" value={form.billing_address} onChange={set('billing_address')} testId="reissue-address" full />
          <FieldRow label="Billing city" value={form.billing_city} onChange={set('billing_city')} testId="reissue-city" />
          <FieldRow label="Billing state" value={form.billing_state} onChange={set('billing_state')} testId="reissue-state" />
          <FieldRow label="Billing pincode" value={form.billing_pincode} onChange={set('billing_pincode')} testId="reissue-pincode" />
          <label className="flex items-center gap-2 col-span-full text-sm text-slate-300 mt-2">
            <input
              type="checkbox"
              data-testid="reissue-resend"
              checked={form.resend_email}
              onChange={(e) => setForm((p) => ({ ...p, resend_email: e.target.checked }))}
            />
            Email the buyer with both PDFs (recommended)
          </label>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy} className="border-slate-700 bg-slate-900 text-slate-200 hover:bg-slate-800">Cancel</Button>
          <Button
            onClick={() => onConfirm(form)}
            disabled={busy || !form.reason || form.reason.trim().length < 3}
            data-testid="reissue-confirm"
            className="bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold"
          >
            {busy ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <RotateCcw className="w-4 h-4 mr-1.5" />}
            Reverse & re-issue
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function FieldRow({ label, value, onChange, testId, mono, full }) {
  return (
    <div className={`space-y-1 ${full ? 'md:col-span-2' : ''}`}>
      <Label className="text-slate-300 text-[10px] uppercase tracking-wider">{label}</Label>
      <Input
        value={value ?? ''} onChange={onChange} data-testid={testId}
        className={`bg-slate-800/50 border-slate-700 text-white h-9 text-sm ${mono ? 'font-mono' : ''}`}
      />
    </div>
  );
}
